import { Center, Heading } from '@chakra-ui/layout';
import {
    Button,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    UnorderedList,
    ListItem,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useState, useRef } from 'react';
import { FaDownload, FaTrash, FaCheck, FaChevronDown, FaExclamationCircle } from 'react-icons/fa';
import SelectedGameContext from '../context/SelectedGameContext';
import { BranchType, BranchData, assignBranch } from '../util/branch';
import { BuildData, downloadAsset, deleteDepot } from '../util/depot';
import {
    ProfileError,
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';

interface DepotBrowserProps {
    isPublic: boolean;
    depots: BuildData[];
    branches: BranchData[];
    branchType: BranchType;
    signal: AbortSignal;
    refreshPage: any;
}

export const DepotBrowser: React.FC<DepotBrowserProps> = ({
    isPublic,
    depots,
    branches,
    branchType,
    signal,
    refreshPage,
}) => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const { isOpen: isDeleteOpen, onClose: onDeleteClose, onOpen: onDeleteOpen } = useDisclosure();
    const { isOpen: isAssignOpen, onClose: onAssignClose, onOpen: onAssignOpen } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [selectedDepot, setSelectedDepot] = useState<BuildData>();
    const [selectedBranch, setSelectedBranch] = useState<BranchData>();
    const [isDeletingDepot, setDeletingDepot] = useState(false);
    const [isAssigningBranch, setAssigningBranch] = useState(false);
    const [errors, setErrors] = useState<ProfileError[]>([]);

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    const renderDepotInformation = (buildData: BuildData) => (
        <Table variant="simple">
            <Tbody>
                {buildData.fileTypes.map((fileType, i) => (
                    <Tr key={`file_${i}`} data-cy="asset-row">
                        <Td>{fileType}</Td>
                        {buildData.gameId !== currLicense.key ? (
                            <Td />
                        ) : (
                            <Td>
                                <Button
                                    onClick={async () => {
                                        try {
                                            await downloadAsset(
                                                fileType,
                                                buildData,
                                                signal,
                                                keycloak.token,
                                            );
                                        } catch (error) {
                                            if (signal.aborted) {
                                                setErrors((errors) =>
                                                    errors.concat(ProfileError.REQUEST_ABORTED),
                                                );
                                            }
                                        }
                                    }}
                                >
                                    <FaDownload style={{ marginRight: '1em' }} />
                                    Download
                                </Button>
                            </Td>
                        )}
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );

    const renderAssignBuildDropDown = (buildData: BuildData) => (
        <Menu>
            <MenuButton as={Button} rightIcon={<FaChevronDown />}>
                -- Select a branch --
            </MenuButton>
            <MenuList>
                {branches.map((branch, i) => (
                    <MenuItem
                        key={`build_${buildData.buildId}_branch_${i}`}
                        onClick={() => {
                            setSelectedDepot(buildData);
                            setSelectedBranch(branch);
                            onAssignOpen();
                        }}
                    >
                        {branch.branchName}
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    );

    const renderAssignedBranchList = (buildData: BuildData) => (
        <UnorderedList>
            {branches
                .filter((branch) => {
                    if (branchType == BranchType.rmb) {
                        return branch.assignBuilds?.rmb?.buildId == buildData.buildId;
                    } else if (branchType == BranchType.actor) {
                        return branch.assignBuilds?.actor?.buildId == buildData.buildId;
                    }
                })
                .map((branch, i) => (
                    <ListItem key={`branch_name_${i}`}>{branch.branchName}</ListItem>
                ))}
        </UnorderedList>
    );

    if (depots.length === 0) {
        if (isPublic) {
            return <Center>{`There are no public ${branchType} depots available.`}</Center>;
        } else {
            return <Center>{`You currently do not have any ${branchType} depots.`}</Center>;
        }
    }
    return (
        <>
            {errors.length > 0 ? (
                <>
                    <Heading textAlign={'center'} marginBottom={'1em'}>
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : (
                <>
                    <Table variant="simple" data-cy="assets-table" key={`${isPublic}_table`}>
                        <Thead>
                            <Tr key={`${isPublic}_table_header`}>
                                <Th>Current</Th>
                                <Th>Build ID</Th>
                                <Th>Last Modified</Th>
                                <Th>Description</Th>
                                <Th>Files</Th>
                                <Th>Set build live on branch...</Th>
                                <Th></Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {depots.map((buildData, i) => {
                                if (!buildData) return null;
                                return (
                                    <Tr key={`depot_${i}`}>
                                        <Td>{renderAssignedBranchList(buildData)}</Td>
                                        <Td>{buildData.buildId}</Td>
                                        <Td>
                                            {new Date(buildData.createDate * 1000).toISOString()}
                                        </Td>
                                        <Td>{buildData.description}</Td>
                                        <Td>{renderDepotInformation(buildData)}</Td>
                                        <Td>{renderAssignBuildDropDown(buildData)}</Td>
                                        {isPublic ? (
                                            <Td />
                                        ) : (
                                            <Td>
                                                {!buildData.inUsed ? (
                                                    <Button
                                                        colorScheme="red"
                                                        variant="solid"
                                                        onClick={() => {
                                                            setSelectedDepot(buildData);
                                                            onDeleteOpen();
                                                        }}
                                                    >
                                                        <FaTrash
                                                            style={{
                                                                marginRight: '1em',
                                                            }}
                                                        />
                                                        Delete
                                                    </Button>
                                                ) : (
                                                    <Button variant="solid" disabled={true}>
                                                        <FaExclamationCircle
                                                            style={{
                                                                marginRight: '1em',
                                                            }}
                                                        />
                                                        In Use
                                                    </Button>
                                                )}
                                            </Td>
                                        )}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>

                    {/* Alert dialogue to confirm choice to delete depot*/}
                    <AlertDialog
                        isOpen={isDeleteOpen}
                        leastDestructiveRef={cancelRef}
                        onClose={onDeleteClose}
                        isCentered
                        motionPreset="slideInBottom"
                    >
                        <AlertDialogOverlay>
                            <AlertDialogContent>
                                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                    Delete build?
                                </AlertDialogHeader>
                                <AlertDialogBody>
                                    Are you sure you want to delete build:{' '}
                                    <b>{selectedDepot?.buildId}</b>?
                                </AlertDialogBody>
                                <AlertDialogFooter>
                                    {!isDeletingDepot ? (
                                        <>
                                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                                Cancel
                                            </Button>
                                            <Button
                                                colorScheme="red"
                                                onClick={async () => {
                                                    try {
                                                        setDeletingDepot(true);
                                                        await deleteDepot(
                                                            selectedDepot!,
                                                            keycloak.token,
                                                        );
                                                        return toast({
                                                            title: 'Success',
                                                            description: `Build ${
                                                                selectedDepot!.buildId
                                                            } deleted.`,
                                                            status: 'success',
                                                            duration: 9000,
                                                            isClosable: true,
                                                        });
                                                    } catch (err) {
                                                        return toast({
                                                            title: 'Error',
                                                            description: `Failed to delete build ${
                                                                selectedDepot!.buildId
                                                            }. Please try again later...`,
                                                            status: 'error',
                                                            duration: 9000,
                                                            isClosable: true,
                                                        });
                                                    } finally {
                                                        setDeletingDepot(false);
                                                        onDeleteClose();
                                                        refreshPage();
                                                    }
                                                }}
                                                ml={3}
                                            >
                                                <FaTrash
                                                    style={{
                                                        marginRight: '1em',
                                                    }}
                                                />{' '}
                                                Delete
                                            </Button>
                                        </>
                                    ) : (
                                        <Spinner data-cy="loading" />
                                    )}
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogOverlay>
                    </AlertDialog>

                    {/* Alert dialogue to confirm assign build to branch*/}
                    <AlertDialog
                        isOpen={isAssignOpen}
                        leastDestructiveRef={cancelRef}
                        onClose={onAssignClose}
                        isCentered
                        motionPreset="slideInBottom"
                    >
                        <AlertDialogOverlay>
                            <AlertDialogContent>
                                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                    Assign build to branch?
                                </AlertDialogHeader>
                                <AlertDialogBody>
                                    Are you sure you want to assign build:{' '}
                                    <b>{selectedDepot?.buildId}</b> to branch:{' '}
                                    <b>{selectedBranch?.branchName}</b>?
                                </AlertDialogBody>
                                <AlertDialogFooter>
                                    {!isAssigningBranch ? (
                                        <>
                                            <Button ref={cancelRef} onClick={onAssignClose}>
                                                Cancel
                                            </Button>
                                            <Button
                                                colorScheme="green"
                                                onClick={async () => {
                                                    try {
                                                        setAssigningBranch(true);
                                                        await assignBranch(
                                                            selectedBranch!.gameId,
                                                            selectedBranch!.branchName,
                                                            branchType,
                                                            selectedDepot!.buildId,
                                                            keycloak.token,
                                                        );
                                                        return toast({
                                                            title: 'Success',
                                                            description: `Build ${selectedDepot?.buildId} assigned to branch ${selectedBranch?.branchName}.`,
                                                            status: 'success',
                                                            duration: 9000,
                                                            isClosable: true,
                                                        });
                                                    } catch (err) {
                                                        return toast({
                                                            title: 'Error',
                                                            description: `Failed to assigned build ${selectedDepot?.buildId} to branch ${selectedBranch?.branchName}.`,
                                                            status: 'error',
                                                            duration: 9000,
                                                            isClosable: true,
                                                        });
                                                    } finally {
                                                        setAssigningBranch(false);
                                                        onAssignClose();
                                                        refreshPage();
                                                    }
                                                }}
                                                ml={3}
                                            >
                                                <FaCheck
                                                    style={{
                                                        marginRight: '1em',
                                                    }}
                                                />{' '}
                                                Assign
                                            </Button>
                                        </>
                                    ) : (
                                        <Spinner data-cy="loading" />
                                    )}
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogOverlay>
                    </AlertDialog>
                </>
            )}
        </>
    );
};
